// Puritas — hero helix: photorealistic liquid double helix (three.js)
// Crystal-clear transmission glass/water, monochrome, high-key studio env.
// Slow vertical rotation (~50s), bubbles drifting up inside the strands
// (per-bubble speeds = parallax), soft "DOF" bubble class, free droplets
// bobbing, ≤3° mouse parallax. Static frame under prefers-reduced-motion.
(function () {
  'use strict';

  var host = document.getElementById('helix-host');
  if (!host || !window.THREE) return;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var renderer;
  try {
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, preserveDrawingBuffer: true });
  } catch (e) { return; }
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.outputEncoding = THREE.sRGBEncoding;
  renderer.toneMapping = THREE.NoToneMapping; // exact background color match
  host.appendChild(renderer.domElement);

  var scene = new THREE.Scene();
  var camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100);
  camera.position.set(0, 0, 11);

  // In-scene paper backdrop: meshes get correct color encoding (linear in,
  // sRGB out), so this plane renders EXACTLY the CSS paper hex — and the
  // transmission pass refracts it at the correct tone. Covers the frustum.
  var backdrop = new THREE.Mesh(
    new THREE.PlaneGeometry(90, 60),
    new THREE.MeshBasicMaterial({ color: 0xffffff })
  );
  backdrop.position.z = -8;
  scene.add(backdrop);
  function syncPaper() {
    var v = getComputedStyle(document.documentElement).getPropertyValue('--paper').trim() || '#f2eee6';
    backdrop.material.color.set(v).convertSRGBToLinear();
    needsRender = true;
  }
  setInterval(syncPaper, 700);

  /* ---------- high-key studio environment (monochrome) ---------- */
  var pmrem = new THREE.PMREMGenerator(renderer);
  var es = new THREE.Scene();
  es.background = new THREE.Color(0xdedbd4).convertSRGBToLinear();
  function envPlane(x, y, z, w, h, lum) {
    var m = new THREE.Mesh(
      new THREE.PlaneGeometry(w, h),
      new THREE.MeshBasicMaterial({ color: new THREE.Color(lum, lum, lum) })
    );
    m.position.set(x, y, z);
    m.lookAt(0, 0, 0);
    es.add(m);
  }
  envPlane(0, 6, 1, 14, 0.9, 4.2);    // overhead slit — crisp top-edge speculars
  envPlane(-5, 1.5, 4, 1.0, 9, 3.4);  // tall key, upper-left
  envPlane(5, 0, -2, 0.8, 10, 1.8);   // counter, right
  envPlane(0, -6, 2, 16, 5, 0.16);    // dark floor — lower-edge shading
  envPlane(-7, 0, -5, 9, 14, 0.1);    // dark flag, back-left — edge darkening
  envPlane(7, 0, -5, 9, 14, 0.1);     // dark flag, back-right
  scene.environment = pmrem.fromScene(es, 0.035).texture;

  var dir = new THREE.DirectionalLight(0xffffff, 0.15);
  dir.position.set(-3, 7, 6);
  scene.add(dir);

  /* ---------- helix ---------- */
  var RADIUS = 1.05;
  var TUBE_R = 0.3;
  var PITCH = 5.2;
  var TURNS = 3;
  var SPAN = TURNS * PITCH;

  function helixPoint(phase, t, target) {
    var ang = phase + t * TURNS * Math.PI * 2;
    return target.set(Math.cos(ang) * RADIUS, (t - 0.5) * SPAN, Math.sin(ang) * RADIUS);
  }
  function helixCurve(phase) {
    var c = new THREE.Curve();
    c.getPoint = function (t, target) { return helixPoint(phase, t, target || new THREE.Vector3()); };
    return c;
  }

  // Organic liquid irregularity, baked ONCE into the geometry (no shaders).
  function organicDisplace(geo, amp) {
    var pos = geo.attributes.position;
    var nor = geo.attributes.normal;
    for (var i = 0; i < pos.count; i++) {
      var x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
      var w = Math.sin(y * 2.3 + x * 1.7)
        + 0.6 * Math.sin(y * 5.1 + z * 2.2)
        + 0.4 * Math.sin((x + z) * 3.4);
      pos.setXYZ(i, x + nor.getX(i) * w * amp, y + nor.getY(i) * w * amp, z + nor.getZ(i) * w * amp);
    }
    geo.computeVertexNormals();
  }

  // Crystal-clear water-glass. Real refraction: the paper background and the
  // BACK strand visibly bend through the front strand.
  // Photoreal water: the body is nearly invisible; all definition lives at
  // the silhouette — a darker refractive band plus a bright internal-
  // reflection line, shaped by fresnel in a fragment-only patch.
  var water = new THREE.MeshPhysicalMaterial({
    color: 0xffffff,
    metalness: 0,
    roughness: 0.03,
    clearcoat: 1,
    clearcoatRoughness: 0.03,
    envMapIntensity: 0.7,
    transparent: true,
    opacity: 1,
    depthWrite: false
  });
  var uEdgeDark = { value: 0.62 };  // dark refractive band strength
  var uEdgeBright = { value: 0.7 }; // bright internal-reflection line
  water.onBeforeCompile = function (shader) {
    shader.uniforms.uEdgeDark = uEdgeDark;
    shader.uniforms.uEdgeBright = uEdgeBright;
    shader.fragmentShader = ('uniform float uEdgeDark;\nuniform float uEdgeBright;\n' + shader.fragmentShader).replace('#include <output_fragment>', [
      'float fres = pow(1.0 - abs(dot(normalize(normal), normalize(vViewPosition))), 1.8);',
      'float vary = 0.72 + 0.28 * sin(vViewPosition.y * 9.0 + vViewPosition.x * 5.0);',
      '#include <output_fragment>',
      'float edgeA = smoothstep(0.16, 0.85, fres);',
      'gl_FragColor.a *= mix(0.05, 0.95, edgeA);',
      'gl_FragColor.rgb = mix(gl_FragColor.rgb, vec3(0.38), smoothstep(0.42, 0.85, fres) * uEdgeDark * vary);',
      'gl_FragColor.rgb += vec3(uEdgeBright) * pow(fres, 8.0) * vary;'
    ].join('\n'));
  };
  var dropletWater = new THREE.MeshPhysicalMaterial({
    color: 0xffffff,
    metalness: 0,
    roughness: 0.03,
    transmission: 1,
    thickness: 1.4,
    ior: 1.33,
    clearcoat: 0.5,
    clearcoatRoughness: 0.03,
    envMapIntensity: 0.5,
    attenuationColor: new THREE.Color(0xd9d9d7),
    attenuationDistance: 3
  });
  // Dark silhouette rim is now handled by the fresnel band in the water
  // material itself (view-dependent — more photographic than a shell mesh).

  // Liquid mercury: bare polished metal. The high-contrast monochrome studio
  // env (bright slits + dark flags) stretches into chrome bands over the
  // organic-displaced surface — classic liquid-chrome look.
  var mercury = new THREE.MeshStandardMaterial({
    color: 0xd4d4d2,
    metalness: 1.0,
    roughness: 0.07,
    envMapIntensity: 1.25
  });

  var tiltGroup = new THREE.Group(); // pose + mouse parallax
  var spinGroup = new THREE.Group(); // slow rotation about vertical axis
  tiltGroup.add(spinGroup);
  scene.add(tiltGroup);
  tiltGroup.rotation.z = -0.12;

  var strandMeshes = [];
  [0, Math.PI].forEach(function (phase) {
    var geo = new THREE.TubeGeometry(helixCurve(phase), 360, TUBE_R, 26, false);
    organicDisplace(geo, 0.03);
    var strand = new THREE.Mesh(geo, water);
    strandMeshes.push(strand);
    spinGroup.add(strand);
  });

  /* ---------- bubbles inside the strands: lensing sprites ---------- */
  // A real macro bubble reads as a dark ring with a bright highlight dot,
  // not a white ball. Camera-facing points with a lens texture, 3 size buckets.
  function bubbleTexture() {
    var c = document.createElement('canvas');
    c.width = c.height = 64;
    var x = c.getContext('2d');
    var g = x.createRadialGradient(32, 32, 2, 32, 32, 30);
    g.addColorStop(0, 'rgba(255,255,255,0.10)');
    g.addColorStop(0.62, 'rgba(245,246,246,0.05)');
    g.addColorStop(0.78, 'rgba(70,76,80,0.55)');
    g.addColorStop(0.9, 'rgba(70,76,80,0.28)');
    g.addColorStop(1, 'rgba(70,76,80,0)');
    x.fillStyle = g;
    x.beginPath(); x.arc(32, 32, 30, 0, 7); x.fill();
    var h = x.createRadialGradient(22, 20, 0, 22, 20, 7);
    h.addColorStop(0, 'rgba(255,255,255,0.95)');
    h.addColorStop(1, 'rgba(255,255,255,0)');
    x.fillStyle = h;
    x.beginPath(); x.arc(22, 20, 7, 0, 7); x.fill();
    return new THREE.CanvasTexture(c);
  }
  var bubTex = bubbleTexture();
  var sphereGeo = new THREE.SphereGeometry(1, 10, 10); // surface droplets

  var clusters = [];
  var ci;
  for (ci = 0; ci < 12; ci++) {
    clusters.push({
      t: Math.random(),
      strand: Math.random() < 0.5 ? 0 : Math.PI,
      speed: 0.005 + Math.random() * 0.007
    });
  }
  function genBubbles(n, clustered) {
    var list = [];
    for (var i = 0; i < n; i++) {
      var b = {};
      if (clustered && Math.random() < 0.75) {
        var c = clusters[(Math.random() * clusters.length) | 0];
        b.strand = c.strand;
        b.t0 = c.t + (Math.random() - 0.5) * 0.035;
        b.speed = c.speed + (Math.random() - 0.5) * 0.002;
      } else {
        b.strand = Math.random() < 0.5 ? 0 : Math.PI;
        b.t0 = Math.random();
        b.speed = 0.004 + Math.random() * 0.009;
      }
      b.off = new THREE.Vector3(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5)
        .normalize().multiplyScalar(TUBE_R * 0.6 * Math.random());
      list.push(b);
    }
    return list;
  }
  function pointCloud(list, size, opacity) {
    var geo = new THREE.BufferGeometry();
    var arr = new Float32Array(list.length * 3);
    geo.setAttribute('position', new THREE.BufferAttribute(arr, 3));
    var mat = new THREE.PointsMaterial({
      size: size, map: bubTex, transparent: true, opacity: opacity,
      depthWrite: false, sizeAttenuation: true
    });
    var pts = new THREE.Points(geo, mat);
    spinGroup.add(pts);
    return { list: list, arr: arr, geo: geo, mat: mat, baseOpacity: opacity };
  }
  function placePoints(cloud, time) {
    var l = cloud.list;
    for (var i = 0; i < l.length; i++) {
      var b = l[i];
      var tt = (b.t0 + time * b.speed) % 1;
      if (tt < 0) tt += 1;
      helixPoint(b.strand, tt, _v).add(b.off);
      cloud.arr[i * 3] = _v.x;
      cloud.arr[i * 3 + 1] = _v.y;
      cloud.arr[i * 3 + 2] = _v.z;
    }
    cloud.geo.attributes.position.needsUpdate = true;
  }
  var microC = pointCloud(genBubbles(120, true), 0.062, 0.75);
  var medC = pointCloud(genBubbles(26, false), 0.13, 0.85);
  var bigC = pointCloud(genBubbles(10, false), 0.2, 0.95);

  // Granular droplets clinging to the strand SURFACE — the sparkle texture
  // that makes macro water photography read as water. Patchy, not uniform.
  var dropMat = new THREE.MeshStandardMaterial({
    color: 0xffffff, metalness: 0, roughness: 0.08,
    envMapIntensity: 1.6, transparent: true, opacity: 0.85
  });
  var SURF_N = 340;
  var surf = new THREE.InstancedMesh(sphereGeo, dropMat, SURF_N);
  spinGroup.add(surf);
  var surfB = [];
  var patches = [];
  for (ci = 0; ci < 16; ci++) {
    patches.push({ t: Math.random(), strand: Math.random() < 0.5 ? 0 : Math.PI });
  }
  for (var si = 0; si < SURF_N; si++) {
    var sb = {};
    if (Math.random() < 0.65) {
      var p = patches[(Math.random() * patches.length) | 0];
      sb.strand = p.strand;
      sb.t0 = p.t + (Math.random() - 0.5) * 0.05;
    } else {
      sb.strand = Math.random() < 0.5 ? 0 : Math.PI;
      sb.t0 = Math.random();
    }
    sb.speed = 0.0003 + Math.random() * 0.0012;
    sb.size = 0.005 + Math.random() * 0.012;
    sb.off = new THREE.Vector3(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5)
      .normalize().multiplyScalar(TUBE_R * (0.94 + Math.random() * 0.12));
    surfB.push(sb);
  }

  var _v = new THREE.Vector3();
  var _m = new THREE.Matrix4();
  function placeBubbles(mesh, list, time) {
    for (var i = 0; i < list.length; i++) {
      var b = list[i];
      var tt = (b.t0 + time * b.speed) % 1;
      if (tt < 0) tt += 1;
      helixPoint(b.strand, tt, _v).add(b.off);
      _m.makeScale(b.size, b.size, b.size);
      _m.setPosition(_v.x, _v.y, _v.z);
      mesh.setMatrixAt(i, _m);
    }
    mesh.instanceMatrix.needsUpdate = true;
  }

  /* ---------- free droplets, bobbing beside the helix ---------- */
  var droplets = [];
  [[-2.3, 1.6, 0.6, 0.13], [-1.7, -1.1, 0.9, 0.085], [-2.9, -0.2, -0.4, 0.06], [-1.3, 2.6, -0.8, 0.1]]
    .forEach(function (d, i) {
      var geo = new THREE.SphereGeometry(d[3], 24, 24);
      organicDisplace(geo, d[3] * 0.06);
      var m = new THREE.Mesh(geo, dropletWater);
      m.position.set(d[0], d[1], d[2]);
      tiltGroup.add(m);
      droplets.push({ mesh: m, y: d[1], phase: i * 1.9 });
    });

  // NOTE: a BokehPass DOF chain was evaluated here and rejected — the blur
  // softened the in-focus fresnel edges that carry the photoreal look.
  // Depth cues come from the soft bubble texture sizes and the CSS edge mask.

  /* ---------- mouse parallax (≤ ~3°) ---------- */
  var mx = 0, my = 0;
  window.addEventListener('mousemove', function (e) {
    mx = (e.clientX / window.innerWidth) * 2 - 1;
    my = (e.clientY / window.innerHeight) * 2 - 1;
  }, { passive: true });

  /* ---------- sizing: right of center, partially cropped ---------- */
  var needsRender = true;
  function resize() {
    var w = host.clientWidth || 1;
    var h = host.clientHeight || 1;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    var halfW = Math.tan(camera.fov * Math.PI / 360) * camera.position.z * camera.aspect;
    tiltGroup.position.x = Math.max(1.2, halfW - 1.15);
    needsRender = true;
  }
  if (window.ResizeObserver) {
    new ResizeObserver(resize).observe(host);
  } else {
    window.addEventListener('resize', resize);
  }
  syncPaper();
  resize();

  /* ---------- loop ---------- */
  var clock = new THREE.Clock();
  var time = 0;
  var visible = true;
  var rafId = null;
  var SPIN = (Math.PI * 2) / 50; // one rotation ≈ 50 s

  function motionOn() {
    return document.body.getAttribute('data-motion') === 'on' && !reduced;
  }

  placePoints(microC, 0);
  placePoints(medC, 0);
  placePoints(bigC, 0);
  placeBubbles(surf, surfB, 0);

  function frame() {
    rafId = null;
    var dt = Math.min(clock.getDelta(), 0.05);
    if (motionOn()) {
      time += dt;
      spinGroup.rotation.y = time * SPIN;
      placePoints(microC, time);
      placePoints(medC, time);
      placePoints(bigC, time);
      placeBubbles(surf, surfB, time);
      for (var i = 0; i < droplets.length; i++) {
        var d = droplets[i];
        d.mesh.position.y = d.y + Math.sin(time * 0.6 + d.phase) * 0.07;
      }
      tiltGroup.rotation.x += ((my * 0.045) - tiltGroup.rotation.x) * 0.04;
      tiltGroup.rotation.z += ((-0.12 + mx * 0.035) - tiltGroup.rotation.z) * 0.04;
      needsRender = true;
    }
    if (needsRender) {
      renderer.render(scene, camera);
      needsRender = false;
    }
    if (visible) rafId = requestAnimationFrame(frame);
  }

  function start() {
    if (rafId === null && visible) {
      clock.getDelta();
      rafId = requestAnimationFrame(frame);
    }
  }

  new IntersectionObserver(function (entries) {
    visible = entries[0].isIntersecting;
    if (visible) start();
  }).observe(host);

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) start();
  });

  /* ---------- finish: water / mercury ---------- */
  var curFinish = 'water';
  var bubbleLevel = 1;
  function applyBubbleVisibility() {
    [microC, medC, bigC].forEach(function (c) {
      c.mat.opacity = Math.min(1, c.baseOpacity * bubbleLevel);
      c.mat.visible = curFinish === 'water' && bubbleLevel > 0.01;
    });
  }
  function setFinish(f) {
    curFinish = f === 'mercury' ? 'mercury' : 'water';
    var liquid = curFinish === 'mercury' ? mercury : water;
    strandMeshes.forEach(function (s) { s.material = liquid; });
    droplets.forEach(function (d) { d.mesh.material = curFinish === 'mercury' ? mercury : dropletWater; });
    surf.material = curFinish === 'mercury' ? mercury : dropMat;
    applyBubbleVisibility();
    needsRender = true;
  }

  /* ---------- tweak API (consumed by the Tweaks panel) ---------- */
  window.__heroHelix = {
    set: function (p) {
      if (typeof p.finish === 'string') setFinish(p.finish);
      if (typeof p.edgeDark === 'number') uEdgeDark.value = p.edgeDark;
      if (typeof p.edgeBright === 'number') uEdgeBright.value = p.edgeBright;
      if (typeof p.bubbles === 'number') {
        bubbleLevel = p.bubbles;
        applyBubbleVisibility();
      }
      if (typeof p.sparkle === 'number') {
        dropMat.opacity = Math.min(1, 0.85 * p.sparkle);
        surf.visible = p.sparkle > 0.01;
      }
      needsRender = true;
      start();
    }
  };

  setFinish('mercury');

  start();
})();
